/* global debounce */

if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      // Fallback sicuro: se il tema NON espone initLazyScript, inizializza subito
      if (typeof window.initLazyScript === 'function') {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        this.init();
      }
    }

    init() {
      // DEBUG: marcatore per capire se questo JS sta girando
      this.setAttribute('data-dd-init', '1');
      console.log('[DD] slideshow_custom.js init');

      // 🔧 wrapper section per gestire --play-state nel CSS della section
      this.section = this.closest('.slideshow-section');

      // riferimenti base
      this.slideshow   = this.querySelector('.slideshow');
      this.slides      = this.querySelectorAll('.slideshow__slide');
      this.nav         = this.querySelector('.slideshow-nav');
      this.pagination  = this.querySelector('.slideshow-pagination');
      this.autoplayBtn = this.querySelector('.autoplay-btn');

      this.rtl              = document.dir === 'rtl';
      this.currentIndex     = 0;
      this.swipeThreshold   = 80;
      this.wheelTriggerWait = 800;

      // Nav (prev/next)
      if (this.nav) {
        this.counterCurrent = this.querySelector('.slideshow-nav__counter-current');
        this.nav.addEventListener('click', this.handleNavClick.bind(this));
      }

      // Pagination (dots/lines)
      if (this.pagination) {
        this.pageBtns = this.querySelectorAll('.page-btn');
        this.pagination.addEventListener('click', this.handlePaginationClick.bind(this));
      }

      // AUTOPLAY BTN (bottone pausa/play)
      if (this.autoplayBtn) {
        this.autoplayBtn.addEventListener('click', (evt) => {
          console.log('[DD] autoplay btn CLICK', evt.target);
          this.togglePlayState(evt);
        });
      }

      // Fallback robusto in cattura (se altri script fermano l’evento)
      this.addEventListener('click', (evt) => {
        const btn = evt.target && evt.target.closest && evt.target.closest('.autoplay-btn');
        if (!btn) return;
        console.log('[DD] capture CLICK on autoplay-btn', evt.target);
        this.togglePlayState({ currentTarget: btn, target: evt.target });
      }, true);

      // Touch / Wheel
      if (typeof theme !== 'undefined' && theme.device && theme.device.hasTouch) {
        this.slideshow.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
        this.slideshow.addEventListener('touchmove', debounce(this.handleTouchMove.bind(this), 200), { passive: true });
      }
      this.slideshow.addEventListener('wheel', this.handleWheel.bind(this), { passive: true });

      // Fine transizioni testo
      this.slideshow.addEventListener('transitionend', SlideShow.handleTransitionend);

      // Stato iniziale visibilità slide
      this.setSlideVisibility();

      // 🔧 Stato iniziale autoplay (icone + CSS var)
      const isAuto = this.dataset.autoplay === 'true';
      if (this.section) {
        this.section.style.setProperty('--play-state', isAuto ? 'running' : 'paused');
      }
      if (!isAuto && this.autoplayBtn) {
        this.autoplayBtn.classList.add('is-paused');
      }

      if (isAuto) this.initAutoPlay();
    }

    initAutoPlay() {
      this.autoplayEnabled = true;
      this.autoplaySpeed   = (Number(this.dataset.speed) || 5) * 1000;

      this.addEventListener('mouseover', this.pauseAutoplay.bind(this));
      this.addEventListener('mouseleave', this.resumeAutoplay.bind(this));
      this.slideshow.addEventListener('focusin', this.pauseAutoplay.bind(this));
      this.slideshow.addEventListener('focusout', this.resumeAutoplay.bind(this));

      this.addObserver();
      this.startAutoplay();
    }

    /**
     * Handles 'click' events on the nav buttons container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (evt.target.matches('.autoplay-btn')) {
        this.togglePlayState(evt);
        return;
      }

      if (evt.target.name === 'next') {
        this.showNextSlide();
      } else if (evt.target.name === 'prev') {
        this.showPrevSlide();
      }
    }

    /**
     * Handles 'click' events on the pagination.
     * @param {object} evt - Event object.
     */
    handlePaginationClick(evt) {
      const btn = evt.target.closest('.page-btn');
      if (!btn) return;
      evt.preventDefault();
      this.autoplayTimeLeft = this.autoplaySpeed;
      this.setActiveSlide(Number(btn.dataset.index));
    }

    /**
     * Handles 'wheel' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleWheel(evt) {
      if (this.blockWheelTrigger) {
        evt.preventDefault();
      } else if (evt.deltaX > 20) {
        evt.preventDefault();
        this.showNextSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      } else if (evt.deltaX < -20) {
        evt.preventDefault();
        this.showPrevSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      }
    }

    /**
     * Handles 'touchstart' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchStart(evt) {
      this.touchStart = evt.changedTouches[0].screenX;
    }

    /**
     * Handles 'touchmove' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchMove(evt) {
      const touchEnd = evt.changedTouches[0].screenX;

      if (touchEnd < this.touchStart - this.swipeThreshold) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      }

      if (touchEnd > this.touchStart + this.swipeThreshold) {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    /**
     * Handles 'transitionend' events on the slideshow.
     * @param {object} evt - Event object.
     */
    static handleTransitionend(evt) {
      if (!evt.target.matches('.has-motion')) return;
      evt.target.classList.remove('transition-out');
    }

    showNextSlide() {
      let index = this.currentIndex + 1;
      if (index === this.slides.length) index = 0;
      this.setActiveSlide(index, 'next');
    }

    showPrevSlide() {
      let index = this.currentIndex - 1;
      if (index < 0) index = this.slides.length - 1;
      this.setActiveSlide(index, 'prev');
    }

    /**
     * Sets the active slide.
     * @param {number} slideIndex - Index of the slide to show.
     * @param {string} slideDirection - Direction for slide transitions.
     */
    setActiveSlide(slideIndex, slideDirection) {
      if (slideIndex === this.currentIndex) return;

      clearTimeout(this.setActiveSlideTimeoutId);
      clearTimeout(this.autoplayTimer);

      // Direzione transizione
      let dir = slideDirection || (slideIndex < this.currentIndex ? 'prev' : 'next');
      if (this.rtl) dir = dir === 'next' ? 'prev' : 'next';
      this.dataset.direction = dir;

      // Overlay testo OUT
      this.slides[this.currentIndex].classList.add('transition-out');

      this.setActiveSlideTimeoutId = setTimeout(() => {
        // Scroll al pannello
        this.slideshow.scrollTo({ left: this.slides[slideIndex].offsetLeft, behavior: 'auto' });

        // Stato attivo
        this.slides[slideIndex].classList.add('is-active');
        this.slides[this.currentIndex].classList.remove('is-active', 'transition-out');

        // Overlay testo della vecchia slide (se presente)
        const transitionedEl = this.slides[this.currentIndex].querySelector('.has-motion');
        if (transitionedEl) transitionedEl.classList.add('transition-out');

        // Reset timer autoplay
        if (this.autoplayEnabled) this.autoplayStartTime = Date.now();

        // Contatore
        if (this.counterCurrent) this.counterCurrent.textContent = slideIndex + 1;

        this.currentIndex = slideIndex;
        this.setSlideVisibility();
        this.updatePagination();

        // Autoplay
        if (this.autoplayEnabled && !this.autoplayPaused) {
          this.autoplayTimeLeft = this.autoplaySpeed;
          this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
        }
      }, 200);
    }

    /**
     * Updates the 'aria-current' attribute of the pagination buttons.
     */
    updatePagination() {
      if (!this.pagination) return;

      this.pageBtns.forEach((el, index) => {
        if (index === this.currentIndex) {
          el.setAttribute('aria-current', 'true');
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }

    stopAutoplay() {
      this.setSlideshowState('paused');
      clearTimeout(this.autoplayTimer);
    }

    startAutoplay() {
      this.autoplayTimeLeft = null;
      this.autoplayStartTime = Date.now();
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
      this.setSlideshowState('running');
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.stopAutoplay();

      if (this.autoplayTimeLeft) {
        this.autoplayTimeLeft -= (Date.now() - this.resumedTime);
      } else {
        this.autoplayTimeLeft = this.autoplaySpeed - (Date.now() - this.autoplayStartTime);
      }
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled || !this.autoplayPaused) return;

      this.resumedTime = Date.now();
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplayTimeLeft);

      this.setSlideshowState('running');
    }

    /**
     * Adds an observer to pause autoplay when the slideshow is not in the viewport.
     */
    addObserver() {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumeAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      });

      observer.observe(this);
    }

    /**
     * Sets 'aria-hidden' and 'tabindex' of the slides and text overlay buttons.
     */
    setSlideVisibility() {
      this.slides.forEach((el, index) => {
        const btns = el.querySelectorAll('a');

        if (index === this.currentIndex) {
          el.setAttribute('aria-hidden', 'false');
          el.removeAttribute('tabindex');

          btns.forEach((btn) => {
            btn.removeAttribute('tabindex');
          });
        } else {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');

          btns.forEach((btn) => {
            btn.setAttribute('tabindex', '-1');
          });
        }
      });
    }

    /**
     * Sets 'aria-live' state of the slideshow and 'play-state' of the pagination animation.
     * @param {string} state - State to set ('running' or 'paused').
     */
    setSlideshowState(state) {
      // 🔧 La variabile CSS deve stare sulla SECTION, non su <slide-show>
      const target = this.section || this;
      target.style.setProperty('--play-state', state);

      this.slideshow.setAttribute('aria-live', state === 'running' ? 'off' : 'polite');
      this.autoplayPaused = state === 'paused';
    }

    /**
     * Toggles the autoplay state (enable/disable).
     * @param {object} evt - Event object.
     */
    togglePlayState(evt) {
      console.log('[DD] togglePlayState BEFORE', {
        enabled: this.autoplayEnabled,
        paused: this.autoplayPaused,
        timer: this.autoplayTimer
      });

      const btn = evt.currentTarget || (evt.target && evt.target.closest && evt.target.closest('.autoplay-btn'));
      if (btn) btn.classList.toggle('is-paused');

      // switch tra abilitato/disabilitato
      this.autoplayEnabled = !this.autoplayEnabled;
      this.dataset.autoplay = this.autoplayEnabled ? 'true' : 'false';

      if (this.autoplayEnabled) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }

      console.log('[DD] togglePlayState AFTER', {
        enabled: this.autoplayEnabled,
        paused: this.autoplayPaused,
        timer: this.autoplayTimer
      });
    }
  }

  customElements.define('slide-show', SlideShow);
}
